import moment from 'moment';

import { MARKET_DATA_TIME_ZONE } from '../constants/date-time.constants';
import { PredefinedMilisecondsTypeEnum } from '../enums/predefined-miliseconds-type.enum';
import { MarketCountryCode, NonWorkingDaysResponse } from '../models/fundamental/types';
import { MarketState, MarketStateInstrumentSnapshot } from '../models/market-data/types';
import { LoggerService } from '../services/logger/Logger.service';
import MarketHoursState from '../store-util/MarketHoursState';

import { isDateMarketSaturdayOrSunday, parseTimeToFloat } from './DateTimeUtils';
import { NullableNumber, NullableString } from './types';

const logger = LoggerService.getLogger();

function getMarketStateByRange(time: number, pre: number, open: number, close: number, afterEnd?: number) {
  let state;

  switch (true) {
    case (time >= pre && time < open):
      state = MarketStateInstrumentSnapshot.IS_PreMarket;
      break;

    case (time >= open && time < close):
      state = MarketStateInstrumentSnapshot.IS_RegularHours;
      break;

    case (afterEnd && time >= close && time < afterEnd):
      state = MarketStateInstrumentSnapshot.IS_AfterMarket;
      break;

    default: {
      const isAfterMidnight = (time >= 0 && time < pre);
      state = (
        isAfterMidnight
          ? MarketStateInstrumentSnapshot.IS_MarketClosedNextDay
          : MarketStateInstrumentSnapshot.IS_MarketClosed
      );
      break;
    }
  }

  return state;
}

export function getDateTime(
  isUtc = true, // eslint-disable-line default-param-last
  operations: ('minus' | 'plus' | 'startOf' | 'endOf' | 'format' | 'unix' | 'tz' | 'local')[] = [], // eslint-disable-line default-param-last
  operationArgs: any[] = [], // eslint-disable-line default-param-last
  customDate?: moment.Moment | Date | NullableString,
) {
  let theFormat = 'YYYY[-]MM[-]DD[T]HH:mm:ss';
  let result: moment.Moment;
  if (customDate) {
    result = isUtc ? moment.utc(customDate) : moment(customDate);
  } else {
    result = isUtc ? moment.utc() : moment();
  }

  if (operations != null) {
    operations
      .forEach((item, index) => {
        const args = operationArgs![index];
        if (args == null) {
          throw new Error(`[DataHelpers] getDateTime - argument at index ${index} not found.`);
        }

        switch (item) {
          case 'minus':
            result.subtract(...args);
            break;

          case 'plus':
            result.add(...args);
            break;

          case 'startOf':
            result.startOf(args[0]);
            break;

          case 'endOf':
            result.endOf(args[0]);
            break;

          case 'format':
            theFormat = args[0];
            break;

          case 'unix':
            theFormat = 'x'; // x is for milliseconds, X - for seconds (reference: https://momentjs.com/docs/#/displaying/)
            break;

          case 'tz':
            result = result.tz(args[0]);
            break;

          case 'local':
            result = result.local();
            break;

          default:
            throw new Error(`[DataHelpers] getDateTime - unknown operation '${item}'.`);
        }
      });
  }

  return result.format(theFormat);
}

export function convertToMarketState(value: MarketStateInstrumentSnapshot | null) {
  switch (value) {
    case MarketStateInstrumentSnapshot.IS_PreMarket: return MarketState.PreMarket;
    case MarketStateInstrumentSnapshot.IS_RegularHours: return MarketState.RegularHours;
    case MarketStateInstrumentSnapshot.IS_AfterMarket: return MarketState.AfterMarket;
    case MarketStateInstrumentSnapshot.IS_MarketClosed:
    case MarketStateInstrumentSnapshot.IS_MarketClosedNextDay:
      return MarketState.MarketClosed;
    default: return null;
  }
}

type MarketStateCacheEntry = {
  minuteKey: number
  marketHoursSource: NonWorkingDaysResponse
  value: MarketStateInstrumentSnapshot | null
}

const marketStateCache = new Map<string, MarketStateCacheEntry>();

const currentMinuteKey = () => Math.floor(Date.now() / PredefinedMilisecondsTypeEnum.oneMinute);

/** Exposed for tests and for callers that change the wall clock or market hours out of band. */
export function invalidateMarketStateCache() {
  marketStateCache.clear();
}

/**
 * The result only changes when the minute or the market-hours config changes, so memoize it per
 * country. `calculateSymbolPrices` and `parseSymbolPriceSourceData` both call this once per symbol on
 * every price update, and each real compute does a `moment.utc().tz().format()` plus two scans over
 * `nonWorkingDays`.
 */
export function calculateMarketState(
  customDateInUtc: NullableString,
  countryCode: NullableString = MarketCountryCode.UnitedStates,
) {
  // A caller-supplied date is not "now", so it must never be served from - or written to - the cache.
  if (customDateInUtc != null) {
    return calculateMarketStateForDate(customDateInUtc, countryCode);
  }

  const cacheKey = countryCode ?? MarketCountryCode.UnitedStates;
  const marketHoursSource = MarketHoursState.get(cacheKey as MarketCountryCode);
  const minuteKey = currentMinuteKey();
  const cached = marketStateCache.get(cacheKey);

  if (cached && cached.minuteKey === minuteKey && cached.marketHoursSource === marketHoursSource) {
    return cached.value;
  }

  const value = calculateMarketStateForDate(customDateInUtc, countryCode);
  marketStateCache.set(cacheKey, { minuteKey, marketHoursSource, value });

  return value;
}

function calculateMarketStateForDate(
  customDateInUtc: NullableString,
  countryCode: NullableString = MarketCountryCode.UnitedStates,
) {
  const { nonWorkingDays, marketHours } = MarketHoursState.get(countryCode as MarketCountryCode);
  const timeZone = marketHours.timeZone || MARKET_DATA_TIME_ZONE;

  const now = getDateTime(true, [ 'tz', 'format' ], [ [ timeZone ], [] ], customDateInUtc);
  const date = now.substring(0, 10);
  const time = now.substring(11, 16);
  const timeAsFloat = parseTimeToFloat(time);
  if (timeAsFloat == null) return null;

  if (isDateMarketSaturdayOrSunday(now)) {
    return MarketStateInstrumentSnapshot.IS_MarketClosedNextDay;
  }

  const isHoliday = nonWorkingDays.some(day => day.date === date && !day.halfDay);
  const isHalfDay = nonWorkingDays.some(day => day.date === date && day.halfDay);

  if (isHoliday) {
    return MarketStateInstrumentSnapshot.IS_MarketClosedNextDay;
  }

  const preMarket = parseTimeToFloat(marketHours.preMarketStart);
  const marketOpen = parseTimeToFloat(marketHours.open);

  let marketClose: NullableNumber = null;
  let afterMarket: NullableNumber = null;

  if (isHalfDay) {
    marketClose = parseTimeToFloat(marketHours.closeForHalfDay);
    afterMarket = parseTimeToFloat(marketHours.afterMarketEndForHalfDay);
  } else {
    marketClose = parseTimeToFloat(marketHours.close);
    afterMarket = parseTimeToFloat(marketHours.afterMarketEnd);
  }

  if (preMarket == null || marketOpen == null || marketClose == null) {
    logger.warn('[calculateMarketState] Invalid market hour values:', { marketHours, isHalfDay });
    return null;
  }

  return getMarketStateByRange(timeAsFloat, preMarket, marketOpen, marketClose, afterMarket || undefined);
}
